import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, Lock, ShieldCheck, Copy, RefreshCw, Download, KeyRound, Info, Mail, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

const ROUTE_MODES = [
  ["internal_drafts", "Internal drafts only (current)", "Drafts are prepared in the Hub; your existing submission process still applies. Nothing is sent or submitted automatically."],
  ["email", "Designated email recipient", "A manual route: the Hub shows the destination address with each approved invoice, but sending remains a manual step until email sending is activated."],
  ["form", "Existing submission form", "A manual link only: configuring a form URL does NOT auto-submit invoices to it. Staff still submit through the form themselves."],
  ["quickbooks_future", "Future QuickBooks integration", "Direct QuickBooks Online integration would use Intuit OAuth to create vendor bills (consultant invoices) after Admin approval. Assessed and not yet built. EdSpeq does not offer a public invoice API; direct QBO integration is the viable path."],
];

const DATA_DOCS = [
  ["Where records are stored", "Schools, contacts, logs, drafts, approvals, evaluations, and invoices live in a dedicated MongoDB database used only by this Operations Hub. It shares nothing with the public CKingEducation website, its member accounts, or Stripe.", "working"],
  ["Audio & attachments", "Voice recordings and uploaded files live in private Emergent object storage. Files are served only through authenticated backend endpoints — there are no public URLs.", "working"],
  ["Access restriction", "JWT httpOnly-cookie auth; consultants see only schools covered by an active, date-bounded assignment and only their own logs/drafts/invoices. School assignment never exposes another consultant's private logs. Admins have full access. Every create/edit/review/approve/submit is written to the audit trail.", "working"],
  ["Search & export", "Contacts and evaluations are searchable/filterable in-app; Admin can export contacts CSV, evaluation responses CSV, and a full JSON export of every collection (Settings → Export).", "working"],
  ["Backup & restore", "The full JSON export is a data export for review/migration — it is NOT a managed backup with point-in-time restore. Recommendation: enable MongoDB scheduled backups at the infrastructure level before production data matters.", "recommendation"],
  ["Retention & deletion", "Recordings can be deleted per-log by Admin (soft-delete; object storage has no hard delete). Retention days setting is recorded here; automated purging is not yet built. Deactivating a school, contact, or consultant never deletes historical records — they remain linked and readable.", "working"],
];

export default function Settings() {
  const { user } = useAuth();
  const [consultants, setConsultants] = useState(null);
  const [team, setTeam] = useState(null);
  const [settings, setSettings] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [invite, setInvite] = useState({ name: "", email: "", role: "consultant" });
  const [inviteResult, setInviteResult] = useState(null);
  const [assignForm, setAssignForm] = useState({ consultant_id: "", school_id: "", engagement_id: "", start_date: "", end_date: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/consultants").then((r) => setConsultants(r.data));
    api.get("/admin/users").then((r) => setTeam(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/organizations").then((r) => setOrgs(r.data));
    api.get("/admin/assignments").then((r) => setAssignments(r.data));
    api.get("/engagements").then((r) => setEngagements(r.data));
  };
  useEffect(() => { load(); }, []);

  const allSchools = orgs.flatMap((o) => o.schools);

  const inviteConsultant = async () => {
    setBusy(true);
    setInviteResult(null);
    try {
      const { data } = await api.post("/admin/consultants", invite);
      setInviteResult(data);
      setInvite({ name: "", email: "", role: "consultant" });
      load();
      toast.success(data.email_status === "sent"
        ? "Invitation created and emailed. The link expires in 7 days and works once."
        : "Invitation created, but the email could not be sent — copy the link and share it manually.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const regenerate = async (c) => {
    try {
      const { data } = await api.post(`/admin/consultants/${c.id}/regenerate-invite`);
      setInviteResult({ ...data, email: c.email });
      toast.success("New secure setup link created.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const resendInvite = async (c) => {
    try {
      const { data } = await api.post(`/admin/consultants/${c.id}/resend-invite`);
      setInviteResult({ ...data, email: c.email });
      load();
      toast.success(data.email_status === "sent"
        ? "Invitation re-sent by email. The previous link is now invalid."
        : "New link created but the email failed — copy and share it manually.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeRole = async (c, role) => {
    const label = role === "admin" ? "Admin (company-wide access)" : "Consultant";
    if (!window.confirm(`Change ${c.name}'s role to ${label}? This applies to their account immediately.`)) return;
    try { await api.patch(`/admin/consultants/${c.id}`, { role }); load(); toast.success("Role updated."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteMember = async (c) => {
    if (!window.confirm(`Permanently delete the account for ${c.name} <${c.email}>?\n\nTheir login access, active sessions, and pending links are revoked immediately. Historical logs, invoices, evaluations, and audit records stay preserved under their name. This cannot be undone.`)) return;
    try { await api.delete(`/admin/consultants/${c.id}`); load(); toast.success(`Account for ${c.name} deleted. Historical records preserved.`); }
    catch (e) { toast.error(apiError(e)); }
  };

  const patchConsultant = async (id, patch) => {
    try { await api.patch(`/admin/consultants/${id}`, patch); load(); toast.success("Consultant updated."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addAssignment = async () => {
    try {
      await api.post("/admin/assignments", assignForm);
      setAssignForm({ consultant_id: "", school_id: "", engagement_id: "", start_date: "", end_date: "" });
      load(); toast.success("Assignment created.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const endAssignment = async (a) => {
    try { await api.patch(`/admin/assignments/${a.id}`, { status: "ended" }); load(); toast.success("Assignment ended. Historical records are preserved."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveSettings = async (patch) => {
    try { await api.put("/admin/settings", patch); load(); toast.success("Settings saved."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changePassword = async () => {
    try { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const download = async (url, filename) => {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = filename; a.click();
    URL.revokeObjectURL(u);
  };

  const deleteSample = async () => {
    if (!window.confirm("Delete ALL sample data (district, schools, contacts, sample consultants, logs, drafts, evaluations, invoices)? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/sample-data");
      toast.success(`Sample data deleted (${Object.values(data.deleted).reduce((a, b) => a + b, 0)} records).`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!consultants || !settings || !team) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="settings-loading">Loading settings…</div>;

  return (
    <div className="max-w-5xl space-y-8" data-testid="settings-page">
      <div className="fade-up">
        <p className="eyebrow">Admin only</p>
        <h1 className="section-title text-3xl mt-1">Settings & Consultant Management</h1>
      </div>

      <div className="card p-6 space-y-4 fade-up-1" data-testid="security-card">
        <h2 className="font-serif font-bold text-lg text-navy flex items-center gap-2"><KeyRound size={17} className="text-terracotta" /> Your account security</h2>
        <p className="text-sm text-[#57534E]">You sign in as <b>christine@ckinged.com</b>. Change your password here anytime (requires your current password). Consultants set their own passwords via secure invitation links below — no passwords are emailed.</p>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div><label className="label">Current password</label>
            <input type="password" className="input" value={pw.current_password} data-testid="current-password-input"
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
          <div><label className="label">New password (min 10 chars)</label>
            <input type="password" className="input" value={pw.new_password} data-testid="new-password-input"
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
          <button className="btn-navy" onClick={changePassword} disabled={!pw.current_password || pw.new_password.length < 10} data-testid="change-password-button">Change password</button>
        </div>
      </div>

      <div className="card p-6 space-y-5 fade-up-1" data-testid="consultant-management-card">
        <h2 className="font-serif font-bold text-lg text-navy">Team members & invitations</h2>
        <p className="text-sm text-[#57534E]">Enter the person's full name and CKingEducation email (it becomes their login and reply-to address). The account is created as a <b>pending invitation</b> and a secure, single-use setup link (valid 7 days) is emailed to them — they set their own password; passwords are never emailed. You can also copy the link and share it manually.</p>
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div><label className="label">Full name</label>
            <input className="input" value={invite.name} data-testid="invite-name-input"
              onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></div>
          <div><label className="label">CKingEducation email</label>
            <input className="input" type="email" value={invite.email} data-testid="invite-email-input"
              onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
          <div><label className="label">Role</label>
            <select className="input" value={invite.role} data-testid="invite-role-select"
              onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
              <option value="consultant">Consultant</option>
              <option value="admin" disabled={!user?.is_owner}>Admin — company-wide access{user?.is_owner ? "" : " (owner only)"}</option>
            </select></div>
          <button className="btn-primary" onClick={inviteConsultant} disabled={busy || !invite.name || !invite.email}
            data-testid="invite-consultant-button"><UserPlus size={15} /> Create & email invitation</button>
        </div>
        {inviteResult && (
          <div className="rounded-lg border border-[#93C5FD] bg-[#EFF6FF] p-4 flex flex-wrap items-center gap-3" data-testid="invite-link-result">
            <div className="text-sm flex-1 min-w-[200px]">
              <b className="text-[#1E40AF]">Setup link for {inviteResult.email}</b>
              {inviteResult.email_status && (
                <span className={`ml-2 ${inviteResult.email_status === "sent" ? "badge-approved" : "badge-nosend"}`} data-testid="invite-email-status">
                  {inviteResult.email_status === "sent" ? "Invitation email sent" : "Email failed — share manually"}</span>)}
              <div className="text-xs break-all mt-1 font-mono">{inviteResult.invite_url}</div>
            </div>
            <button className="btn-outline !py-1.5" data-testid="copy-invite-link"
              onClick={() => { navigator.clipboard.writeText(inviteResult.invite_url); toast.success("Link copied."); }}>
              <Copy size={14} /> Copy link</button>
          </div>
        )}
        <div className="space-y-3">
          {team.map((c) => (
            <div key={c.id} className="rounded-lg border border-[#E7E1D4] p-4 space-y-3" data-testid={`consultant-row-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-bold text-navy">{c.name}</span>
                  <span className="ml-2 text-xs text-[#78716C]">{c.email}</span>
                  <span className={`ml-2 ${c.role === "admin" ? "badge-approved" : "badge-neutral"}`} data-testid={`member-role-${c.id}`}>
                    {c.role === "admin" ? "Admin" : "Consultant"}</span>
                  {c.is_owner && <span className="badge-draft ml-2" data-testid={`member-owner-${c.id}`}>Owner</span>}
                  {c.is_sample && <span className="badge-neutral ml-2">Sample</span>}
                  <span className={`ml-2 ${c.invite_status === "pending" ? "badge-draft" : c.active ? "badge-approved" : "badge-nosend"}`}
                    data-testid={`consultant-status-${c.id}`}>{c.invite_status === "pending" ? "Pending invitation" : c.active ? "Active" : "Deactivated"}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {c.invite_status === "pending" && (
                    <button className="btn-ghost !py-1" onClick={() => resendInvite(c)} data-testid={`resend-invite-${c.id}`}>
                      <Mail size={13} /> Resend invitation email</button>)}
                  {(c.role !== "admin" || user?.is_owner) && !c.is_owner && (
                    <button className="btn-ghost !py-1" onClick={() => regenerate(c)} data-testid={`regen-invite-${c.id}`}>
                      <RefreshCw size={13} /> New setup / reset link</button>)}
                  {user?.is_owner && c.id !== user?.id && (
                    <button className="btn-ghost !py-1 text-[#1E40AF]" onClick={() => changeRole(c, c.role === "admin" ? "consultant" : "admin")}
                      data-testid={`change-role-${c.id}`}>
                      {c.role === "admin"
                        ? <><ArrowDownCircle size={13} /> Make Consultant</>
                        : <><ArrowUpCircle size={13} /> Make Admin</>}</button>)}
                  {(c.role !== "admin" || user?.is_owner) && c.id !== user?.id && !c.is_owner && (
                    <button className={`btn-ghost !py-1 ${c.active ? "text-[#991B1B]" : "text-[#065F46]"}`}
                      onClick={() => patchConsultant(c.id, { active: !c.active })} data-testid={`toggle-consultant-${c.id}`}>
                      {c.active ? "Deactivate" : "Reactivate"}</button>)}
                  {(c.role !== "admin" || user?.is_owner) && c.id !== user?.id && !c.is_owner && (
                    <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => deleteMember(c)} data-testid={`delete-member-${c.id}`}>
                      <Trash2 size={13} /> Delete account</button>)}
                </div>
              </div>
              {c.role === "consultant" && (<>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div><label className="label">Reply-to email (used in follow-ups)</label>
                  <input className="input !py-1.5 text-xs" defaultValue={c.reply_to_email || c.email} data-testid={`reply-to-${c.id}`}
                    onBlur={(e) => e.target.value !== (c.reply_to_email || c.email) && patchConsultant(c.id, { reply_to_email: e.target.value })} /></div>
                <div><label className="label">Daily rate ($)</label>
                  <input className="input !py-1.5 text-xs" type="number" min="0" placeholder="Not set" defaultValue={c.daily_rate ?? ""} data-testid={`daily-rate-${c.id}`}
                    onBlur={(e) => patchConsultant(c.id, { daily_rate: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                <div><label className="label">Half-day rate ($)</label>
                  <input className="input !py-1.5 text-xs" type="number" min="0" placeholder="Not set" defaultValue={c.half_day_rate ?? ""} data-testid={`half-day-rate-${c.id}`}
                    onBlur={(e) => patchConsultant(c.id, { half_day_rate: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                <div><label className="label">Hourly rate ($)</label>
                  <input className="input !py-1.5 text-xs" type="number" min="0" placeholder="Not set" defaultValue={c.hourly_rate ?? ""} data-testid={`hourly-rate-${c.id}`}
                    onBlur={(e) => patchConsultant(c.id, { hourly_rate: e.target.value === "" ? null : Number(e.target.value) })} /></div>
              </div>
              <p className="text-[11px] text-[#78716C]">Blank rates are flagged on invoices — amounts are never invented. Rates you set here are the agreed billing units used for that consultant's invoice drafts.</p>
              </>)}
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6 space-y-4 fade-up-2" data-testid="assignments-card">
        <h2 className="font-serif font-bold text-lg text-navy">Consultant assignments (school + engagement + dates)</h2>
        <p className="text-sm text-[#57534E]">A consultant sees only schools with an active assignment, and never another consultant's private logs. Ending an assignment preserves all historical service records.</p>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div><label className="label">Consultant</label>
            <select className="input" value={assignForm.consultant_id} data-testid="assign-consultant-select"
              onChange={(e) => setAssignForm({ ...assignForm, consultant_id: e.target.value })}>
              <option value="">Select…</option>
              {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
          <div><label className="label">School</label>
            <select className="input" value={assignForm.school_id} data-testid="assign-school-select"
              onChange={(e) => setAssignForm({ ...assignForm, school_id: e.target.value })}>
              <option value="">Select…</option>
              {allSchools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div><label className="label">Engagement</label>
            <select className="input" value={assignForm.engagement_id} data-testid="assign-engagement-select"
              onChange={(e) => setAssignForm({ ...assignForm, engagement_id: e.target.value })}>
              <option value="">Select…</option>
              {engagements.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}</select></div>
          <div><label className="label">Start</label>
            <input type="date" className="input" value={assignForm.start_date} data-testid="assign-start-input"
              onChange={(e) => setAssignForm({ ...assignForm, start_date: e.target.value })} /></div>
          <div><label className="label">End (optional)</label>
            <input type="date" className="input" value={assignForm.end_date} data-testid="assign-end-input"
              onChange={(e) => setAssignForm({ ...assignForm, end_date: e.target.value })} /></div>
          <button className="btn-primary" onClick={addAssignment} disabled={!assignForm.consultant_id || !assignForm.school_id}
            data-testid="assign-save-button">Assign</button>
        </div>
        <table className="w-full">
          <thead><tr><th className="table-th">Consultant</th><th className="table-th">School</th>
            <th className="table-th">Engagement</th><th className="table-th">Dates</th><th className="table-th">Status</th><th className="table-th"></th></tr></thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                <td className="table-td font-bold">{a.consultant_name}</td>
                <td className="table-td">{a.school_name}</td>
                <td className="table-td text-xs">{a.engagement_title || "—"}</td>
                <td className="table-td text-xs">{a.start_date || "…"} → {a.end_date || "open"}</td>
                <td className="table-td"><span className={a.status === "active" ? "badge-approved" : "badge-neutral"}>{a.status}</span></td>
                <td className="table-td">{a.status === "active" && (
                  <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" onClick={() => endAssignment(a)} data-testid={`end-assignment-${a.id}`}>End</button>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 fade-up-2">
        <div className="card p-6 space-y-4" data-testid="sender-config-card">
          <h2 className="font-serif font-bold text-lg text-navy">Email sender configuration (pre-activation)</h2>
          <p className="text-sm text-[#57534E]">External sending is <b>active</b>. Delivery runs through the platform's verified sender (the From address is managed by the platform, not configurable here). Follow-ups show the consultant's name with their own address as Reply-To; invitation emails use the address below as Reply-To. Display name: <b>CKingEducation Operations Hub</b>; signature: “{settings.sender_signature_line}”.</p>
          <div className="grid gap-3">
            <div><label className="label">Verified sender address (central, e.g. ops@ckinged.com)</label>
              <input className="input" defaultValue={settings.sender_from_address} placeholder="Not configured — required before activation"
                data-testid="sender-address-input" onBlur={(e) => saveSettings({ sender_from_address: e.target.value })} /></div>
            <div><label className="label">Sender name</label>
              <input className="input" defaultValue={settings.sender_from_name} placeholder="e.g. CKingEducation Operations"
                data-testid="sender-name-input" onBlur={(e) => saveSettings({ sender_from_name: e.target.value })} /></div>
            <div><label className="label">Public app base URL (for evaluation links)</label>
              <input className="input" defaultValue={settings.public_base_url} placeholder="https://… (auto-detected from your browser when possible)"
                data-testid="public-base-url-input" onBlur={(e) => saveSettings({ public_base_url: e.target.value })} /></div>
          </div>
          <span className="badge-approved" data-testid="email-sending-on-badge">External sending: ON (platform-verified sender)</span>
        </div>

        <div className="card p-6 space-y-4" data-testid="invoice-routing-card">
          <h2 className="font-serif font-bold text-lg text-navy">Invoice routing</h2>
          <p className="text-sm text-[#57534E]">Consultant invoices (to CKingEducation for payment) are separate from client follow-up emails. Choose where approved invoices go when routing is activated:</p>
          <div className="space-y-2">
            {ROUTE_MODES.map(([mode, label, desc]) => (
              <label key={mode} className={`block rounded-lg border p-3.5 cursor-pointer transition-colors ${settings.invoice_route.mode === mode ? "border-terracotta bg-[#FDF3EE]" : "border-[#E7E1D4]"}`}
                data-testid={`invoice-route-${mode}`}>
                <div className="flex items-center gap-2.5">
                  <input type="radio" name="invoice_route" className="accent-[#B85C37]"
                    checked={settings.invoice_route.mode === mode}
                    onChange={() => saveSettings({ invoice_route: { ...settings.invoice_route, mode } })} />
                  <span className="text-sm font-bold text-navy">{label}</span>
                </div>
                <p className="text-xs text-[#78716C] mt-1 ml-6">{desc}</p>
              </label>
            ))}
          </div>
          {settings.invoice_route.mode === "email" && (
            <div><label className="label">Destination email</label>
              <input className="input" defaultValue={settings.invoice_route.destination_email} placeholder="accounts@ckinged.com"
                data-testid="invoice-dest-email" onBlur={(e) => saveSettings({ invoice_route: { ...settings.invoice_route, destination_email: e.target.value } })} /></div>
          )}
          {settings.invoice_route.mode === "form" && (
            <div><label className="label">Submission form URL (manual link only)</label>
              <input className="input" defaultValue={settings.invoice_route.form_url} placeholder="https://…"
                data-testid="invoice-form-url" onBlur={(e) => saveSettings({ invoice_route: { ...settings.invoice_route, form_url: e.target.value } })} /></div>
          )}
        </div>
      </div>

      <div className="card p-6 space-y-4 fade-up-3" data-testid="data-storage-card">
        <h2 className="font-serif font-bold text-lg text-navy">Data, storage & recovery — what actually exists</h2>
        <div className="space-y-3">
          {DATA_DOCS.map(([title, text, kind]) => (
            <div key={title} className="flex gap-3 items-start">
              <span className={`shrink-0 mt-0.5 ${kind === "working" ? "badge-approved" : "badge-draft"}`}>{kind === "working" ? "Working" : "Recommendation"}</span>
              <div>
                <div className="text-sm font-bold text-navy">{title}</div>
                <p className="text-xs text-[#57534E] leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F3EFE6]">
          <button className="btn-outline !py-1.5" onClick={() => download("/admin/export/contacts.csv", "contacts-export.csv")} data-testid="export-contacts-button"><Download size={14} /> Contacts CSV</button>
          <button className="btn-outline !py-1.5" onClick={() => download("/admin/export/full", "ops-hub-export.json")} data-testid="export-full-button"><Download size={14} /> Full JSON export</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 fade-up-3">
        <div className="card p-6 space-y-4" data-testid="retention-card">
          <h2 className="font-serif font-bold text-lg text-navy">Recording & attachment retention</h2>
          <p className="text-sm text-[#57534E]">Recordings are stored privately and never shared in external drafts. Delete any recording from its log's detail page. Automated purging by age is not built yet — the retention period is policy, enforced manually for now.</p>
          <div><label className="label">Retention period (days)</label>
            <input className="input w-32" type="number" min="1" defaultValue={settings.retention_days}
              data-testid="retention-days-input" onBlur={(e) => saveSettings({ retention_days: Number(e.target.value) })} /></div>
        </div>
        <div className="card p-6 space-y-4" data-testid="sample-data-card">
          <div className="flex items-center gap-2">
            <ShieldCheck size={17} className="text-terracotta" />
            <h2 className="font-serif font-bold text-lg text-navy">Sample data</h2>
            {settings.sample_data_present
              ? <span className="badge-draft" data-testid="sample-data-present-badge">Present</span>
              : <span className="badge-approved" data-testid="sample-data-gone-badge">Removed</span>}
          </div>
          <p className="text-sm text-[#57534E]">All seeded fictional records are marked <b>Sample</b> — district, schools, contacts, consultants, engagement, logs, drafts, evaluations, and invoices.</p>
          <button className="btn-ghost !text-[#991B1B] border border-[#FCA5A5]" onClick={deleteSample}
            disabled={!settings.sample_data_present} data-testid="delete-sample-data-button">
            <Trash2 size={15} /> Delete all sample data</button>
        </div>
      </div>

      <div className="card p-6 space-y-3 fade-up-3" data-testid="future-features-card">
        <h2 className="font-serif font-bold text-lg text-navy">Activation status</h2>
        {[["External email sending", "Active — invitations, password resets, and consultant-approved follow-ups send via the platform-verified sender. Consultants send their own approved follow-ups; fictional sample contacts are blocked.", true],
          ["QuickBooks transfer", "Assessed: direct QuickBooks Online OAuth integration can create vendor bills after Admin approval. EdSpeq has no public invoice API. Not built yet — invoices stay internal.", false],
          ["Evaluation identity tracking", "Evaluation links are shared per session with no teacher identity collected. Recipient-specific tracked links require your explicit approval before being built.", false],
          ["Automated reminders & notifications", "Not built; nothing sends automatically.", false]].map(([label, desc, on], i) => (
          <div key={label} className="flex items-start justify-between gap-4 rounded-lg border border-[#E7E1D4] p-4" data-testid={`future-feature-${i}`}>
            <div>
              <div className="text-sm font-bold text-navy flex items-center gap-2"><Lock size={13} className="text-[#92400E]" /> {label}</div>
              <div className="text-xs text-[#78716C] mt-0.5">{desc}</div>
            </div>
            <span className={on ? "badge-approved shrink-0" : "badge-neutral shrink-0"}>{on ? "On" : "Off"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
